import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { templateApi } from '../../api'
import type { ApiError } from '../../api/client'
import type { DayOfWeek, TrainDay, TrainingSetTemplateCreateRequest, TrainingSetTemplateUpdateRequest } from '../../types/api'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { parseDuration, toSeconds } from '../../utils/duration'
import { useSubscription } from '../../hooks/useSubscription'
import { Button, Card, DashedPlaceholder } from '../ui'

export default function CreateEditTemplate() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id
  const theme = useTheme()
  const { t } = useTranslation()
  const { canAddTemplate } = useSubscription()

  const DAYS_OF_WEEK: { value: DayOfWeek; abbr: string; label: string }[] = [
    { value: 'MONDAY',    abbr: t.common.dayAbbr.mon, label: t.common.day.monday },
    { value: 'TUESDAY',   abbr: t.common.dayAbbr.tue, label: t.common.day.tuesday },
    { value: 'WEDNESDAY', abbr: t.common.dayAbbr.wed, label: t.common.day.wednesday },
    { value: 'THURSDAY',  abbr: t.common.dayAbbr.thu, label: t.common.day.thursday },
    { value: 'FRIDAY',    abbr: t.common.dayAbbr.fri, label: t.common.day.friday },
    { value: 'SATURDAY',  abbr: t.common.dayAbbr.sat, label: t.common.day.saturday },
    { value: 'SUNDAY',    abbr: t.common.dayAbbr.sun, label: t.common.day.sunday },
  ]

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedDays, setSelectedDays] = useState<Map<DayOfWeek, string>>(new Map())
  const [defaultStartTime, setDefaultStartTime] = useState('')
  const [plannedTrainings, setPlannedTrainings] = useState<number | ''>('')
  const [durationHours, setDurationHours] = useState<number | ''>('')
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('')
  const [maxSkipCount, setMaxSkipCount] = useState<number | ''>('')

  useEffect(() => {
    if (isEditMode) {
      loadTemplate()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function loadTemplate() {
    if (!id) return
    setLoading(true)
    try {
      const template = await templateApi.getTemplate(parseInt(id))
      setName(template.name)
      setDescription(template.description || '')
      setDefaultStartTime(template.defaultStartTime || '')
      setPlannedTrainings(template.plannedTrainings || '')
      setMaxSkipCount(template.maxSkipCount || '')
      if (template.duration) {
        const { hours, minutes } = parseDuration(template.duration)
        setDurationHours(hours || '')
        setDurationMinutes(minutes || '')
      }
      if (template.days) {
        const daysMap = new Map<DayOfWeek, string>()
        template.days.forEach(day => {
          daysMap.set(day.day, day.startTime || '')
        })
        setSelectedDays(daysMap)
      }
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to load template')
    } finally {
      setLoading(false)
    }
  }

  function handleDayToggle(day: DayOfWeek) {
    setSelectedDays(prev => {
      const next = new Map(prev)
      if (next.has(day)) {
        next.delete(day)
      } else {
        next.set(day, defaultStartTime)
      }
      return next
    })
  }

  function handleDayTimeChange(day: DayOfWeek, time: string) {
    setSelectedDays(prev => new Map(prev).set(day, time))
  }

  function validateForm(): string | null {
    if (!name.trim()) {
      return t.templateForm.errors.nameRequired
    }
    if (name.length > 100) {
      return t.templateForm.errors.nameTooLong
    }
    if (description.length > 255) {
      return t.templateForm.errors.descriptionTooLong
    }
    const hasAnyProperty =
      selectedDays.size > 0 ||
      defaultStartTime ||
      plannedTrainings !== '' ||
      durationHours !== '' ||
      durationMinutes !== '' ||
      maxSkipCount !== ''
    if (!hasAnyProperty) {
      return t.templateForm.errors.noPropertiesSet
    }
    if (plannedTrainings !== '' && (plannedTrainings < 1 || plannedTrainings > 365)) {
      return t.templateForm.errors.plannedTrainingsRange
    }
    const totalHours = (durationHours || 0) + (durationMinutes || 0) / 60
    if (totalHours > 12) {
      return t.templateForm.errors.durationTooLong
    }
    if (maxSkipCount !== '') {
      if (maxSkipCount < 0 || maxSkipCount > 365) {
        return t.templateForm.errors.maxSkipCountRange
      }
      if (plannedTrainings !== '' && maxSkipCount > plannedTrainings) {
        return t.templateForm.errors.maxSkipExceedsPlanned
      }
    }
    return null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isEditMode && !canAddTemplate) {
      navigate('/subscription')
      return
    }
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const days: TrainDay[] = Array.from(selectedDays.entries()).map(([day, time]) => ({
        day,
        startTime: time || null,
      }))
      let duration: number | undefined
      if (durationHours !== '' || durationMinutes !== '') {
        duration = toSeconds(durationHours || 0, durationMinutes || 0)
      }
      const request = {
        name: name.trim(),
        description: description.trim() || undefined,
        defaultStartTime: defaultStartTime || undefined,
        plannedTrainings: plannedTrainings !== '' ? plannedTrainings : undefined,
        duration,
        maxSkipCount: maxSkipCount !== '' ? maxSkipCount : undefined,
        days: days.length > 0 ? days : undefined,
      }
      if (isEditMode) {
        await templateApi.updateTemplate(parseInt(id!), {
          ...request,
          id: parseInt(id!),
        } as TrainingSetTemplateUpdateRequest)
      } else {
        await templateApi.createTemplate(request as TrainingSetTemplateCreateRequest)
      }
      navigate('/templates')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || `Failed to ${isEditMode ? 'update' : 'create'} template`)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="p-5 text-center opacity-60 pt-16">{t.common.loading}</div>
  }

  const activeDays = DAYS_OF_WEEK.filter(d => selectedDays.has(d.value))

  const sectionLabel = {
    fontSize: '11px' as const,
    fontWeight: 700 as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.text.muted,
    opacity: 0.7,
    marginBottom: '10px',
  }

  const rowBorder = { borderBottom: `1px solid ${theme.cardBorder}` }

  return (
    <div className="p-5 pb-24">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {isEditMode ? t.templateForm.editTitle : t.templateForm.createTitle}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.templateForm.subtitle}
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl p-4 mb-6 flex items-start gap-3"
          style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* DETAILS */}
        <p style={sectionLabel}>{t.templateForm.sections.details}</p>
        <Card className="p-0 overflow-hidden mb-6">
          <div className="px-4 py-3.5" style={rowBorder}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold" style={{ color: theme.text.muted }}>
                {t.templateForm.fields.name} *
              </span>
              <span className="text-xs" style={{ color: theme.text.muted, opacity: 0.5 }}>
                {name.length}/100
              </span>
            </div>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t.templateForm.placeholders.name}
              className="w-full bg-transparent outline-none text-sm"
              style={{ color: theme.text.primary }}
              maxLength={100}
            />
          </div>
          <div className="px-4 py-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold" style={{ color: theme.text.muted }}>
                {t.templateForm.fields.description}
              </span>
              <span className="text-xs" style={{ color: theme.text.muted, opacity: 0.5 }}>
                {description.length}/255
              </span>
            </div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t.templateForm.placeholders.description}
              className="w-full bg-transparent outline-none text-sm resize-none"
              style={{ color: theme.text.primary, minHeight: '60px' }}
              maxLength={255}
            />
          </div>
        </Card>

        {/* SCHEDULE */}
        <p style={sectionLabel}>{t.templateForm.sections.schedule}</p>

        {/* Default start time */}
        <Card className="p-0 overflow-hidden mb-4">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <span
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </span>
              <div>
                <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                  {t.templateForm.fields.defaultStartTime}
                </p>
                <p className="text-[11px]" style={{ color: theme.text.muted }}>
                  {t.templateForm.hints.defaultStartTime}
                </p>
              </div>
            </div>
            <input
              type="time"
              value={defaultStartTime}
              onChange={e => setDefaultStartTime(e.target.value)}
              className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
              style={{ color: theme.text.primary }}
            />
          </div>
        </Card>

        {/* Day picker */}
        <div className="flex justify-between gap-1 mb-3">
          {DAYS_OF_WEEK.map(({ value, abbr }) => {
            const active = selectedDays.has(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleDayToggle(value)}
                className="flex-1 flex flex-col items-center gap-1 py-1 select-none"
              >
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold transition-all active:scale-90"
                  style={active ? {
                    backgroundColor: theme.buttonColor,
                    color: theme.buttonTextColor,
                    boxShadow: `0 4px 12px ${theme.buttonColor}40`,
                  } : {
                    backgroundColor: theme.cardBg,
                    border: `1px solid ${theme.cardBorder}`,
                    color: theme.text.muted,
                  }}
                >
                  {abbr.charAt(0)}
                </span>
                <span
                  className="text-[10px] font-medium"
                  style={{ color: active ? theme.accent.text : theme.text.muted, opacity: active ? 1 : 0.5 }}
                >
                  {abbr}
                </span>
              </button>
            )
          })}
        </div>

        {/* Summary badge */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: selectedDays.size > 0 ? `${theme.buttonColor}18` : theme.cardBg,
              border: `1px solid ${selectedDays.size > 0 ? `${theme.buttonColor}40` : theme.cardBorder}`,
              color: selectedDays.size > 0 ? theme.accent.text : theme.text.muted,
            }}
          >
            {selectedDays.size} {t.templateForm.daysPerWeek}
          </span>
          {selectedDays.size > 0 && (
            <span className="text-xs" style={{ color: theme.text.muted }}>
              {t.templateForm.tapToAdjustTime}
            </span>
          )}
        </div>

        {/* Per-day times or empty state */}
        {activeDays.length > 0 ? (
          <Card className="p-0 overflow-hidden mb-6">
            {activeDays.map((day, idx) => (
              <div
                key={day.value}
                className="flex items-center justify-between px-4 py-3.5"
                style={idx < activeDays.length - 1 ? rowBorder : undefined}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                    style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
                  >
                    {day.abbr.charAt(0)}
                  </span>
                  <span className="text-sm font-medium" style={{ color: theme.text.primary }}>
                    {day.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted, opacity: 0.6 }}>
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <input
                    type="time"
                    value={selectedDays.get(day.value) || ''}
                    onChange={e => handleDayTimeChange(day.value, e.target.value)}
                    className="text-sm font-semibold bg-transparent border-none outline-none"
                    style={{ color: theme.text.primary }}
                  />
                </div>
              </div>
            ))}
          </Card>
        ) : (
          <DashedPlaceholder
            className="mb-6"
            title={t.templateForm.noTrainingDays.title}
            hint={t.templateForm.noTrainingDays.hint}
          />
        )}

        {/* GOALS */}
        <p style={sectionLabel}>{t.templateForm.sections.goals}</p>
        <Card className="p-0 overflow-hidden mb-6">
          <div className="flex items-center justify-between px-4 py-3.5" style={rowBorder}>
            <div>
              <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                {t.templateForm.fields.plannedTrainings}
              </p>
              <p className="text-[11px]" style={{ color: theme.text.muted }}>
                {t.templateForm.hints.plannedTrainings}
              </p>
            </div>
            <input
              type="number"
              value={plannedTrainings}
              onChange={e => setPlannedTrainings(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="—"
              className="w-20 text-sm font-semibold bg-transparent border-none outline-none text-right"
              style={{ color: theme.text.primary }}
              min={1}
              max={365}
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3.5">
            <div>
              <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                {t.templateForm.fields.duration}
              </p>
              <p className="text-[11px]" style={{ color: theme.text.muted }}>
                {t.templateForm.hints.duration}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={durationHours}
                onChange={e => setDurationHours(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="0"
                className="w-10 text-sm font-semibold bg-transparent border-none outline-none text-right"
                style={{ color: theme.text.primary }}
                min={0}
                max={12}
              />
              <span className="text-xs" style={{ color: theme.text.muted }}>{t.templateForm.durationUnits.hours}</span>
              <input
                type="number"
                value={durationMinutes}
                onChange={e => setDurationMinutes(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="0"
                className="w-10 text-sm font-semibold bg-transparent border-none outline-none text-right"
                style={{ color: theme.text.primary }}
                min={0}
                max={59}
              />
              <span className="text-xs" style={{ color: theme.text.muted }}>{t.templateForm.durationUnits.minutes}</span>
            </div>
          </div>
        </Card>

        {/* SKIP RULES */}
        <p style={sectionLabel}>{t.templateForm.sections.skipRules}</p>
        <Card className="p-0 overflow-hidden mb-8">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex-1 mr-4">
              <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                {t.templateForm.fields.maxSkipCount}
              </p>
              <p className="text-[11px]" style={{ color: theme.text.muted }}>
                {t.templateForm.hints.maxSkipCount}
              </p>
            </div>
            <input
              type="number"
              value={maxSkipCount}
              onChange={e => {
                const v = e.target.value
                if (v === '') {
                  setMaxSkipCount('')
                } else {
                  const n = parseInt(v, 10)
                  if (!isNaN(n)) setMaxSkipCount(n)
                }
              }}
              placeholder="—"
              className="w-20 text-sm font-semibold bg-transparent border-none outline-none text-right"
              style={{ color: theme.text.primary }}
              min={0}
              max={365}
            />
          </div>
        </Card>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={submitting}
        >
          {submitting
            ? t.common.saving
            : isEditMode
              ? t.templateForm.actions.update
              : t.templateForm.actions.create}
        </Button>
      </form>
    </div>
  )
}
